import {useSelector} from 'react-redux';

import {domainMonitorAPI} from 'features/domain/api/domainMonitorAPI';
import {
    DOMAIN_MONITORS_DEFAULT_SORT,
    DOMAIN_MONITORS_ORDER_FIELDS,
    getDomainMonitorsFilterArray,
} from 'features/domain/lib/domainFilters';
import {useMonitorList, IMonitorListState} from 'entities/monitor';
import {useCreateGate} from 'entities/tariff';
import {IUserOutput, userSelector, useHasTariffLimit} from 'entities/user';
import IOrderBy from 'shared/model/IOrderBy';
import IPagination from 'shared/api/model/IPagination';
import useListParams from 'shared/lib/hooks/useListParams';
import {getPollingInterval} from 'shared/api/pollingHelper';

import IDomainMonitorOutput from './DomainMonitor/IDomainMonitor';
import {
    clearAll,
    domainMonitorSelectedIdsSelector,
    isSelectedAllSelector,
    selectAllOnPage,
} from './domainMonitorSlice';
import useCanCreateDomainMonitor, {useDomainMonitorsLimit} from './useCanCreateDomainMonitor';

export interface IUseDomainMonitors {
    user: IUserOutput | null,
    sort: IOrderBy | undefined,
    pagination: IPagination<IDomainMonitorOutput> | undefined,
    isLoading: boolean,
    isFetching: boolean,
    refetch: () => void,
    activeCount: number | undefined,
    maxDomainMonitors: number | undefined,
    list: IMonitorListState,
    handleCreate: () => void,
    isCreateOpen: boolean,
    isExceededOpen: boolean,
    closeCreate: () => void,
}

export const useDomainMonitors = (): IUseDomainMonitors => {
    const {args, sort, filters, hasFilters} = useListParams({
        getFilters: getDomainMonitorsFilterArray,
        defaultSort: DOMAIN_MONITORS_DEFAULT_SORT,
        sortFields: DOMAIN_MONITORS_ORDER_FIELDS,
    });
    const create = useCreateGate(useCanCreateDomainMonitor());
    const {activeCount, maxCount} = useDomainMonitorsLimit(getPollingInterval());

    const user = useSelector(userSelector);

    const hasAccess = useHasTariffLimit('domain_monitor_max_count_limit');

    const {data: pagination, isLoading, isFetching, refetch} =
        domainMonitorAPI.useGetDomainMonitorsQuery(args, {
            skip: !hasAccess,
            pollingInterval: getPollingInterval(),
        });

    const [getAllDomainMonitorIds] = domainMonitorAPI.useLazyGetAllDomainMonitorIdsQuery();

    const list = useMonitorList({
        pagination,
        isLoading,
        tariffField: 'domain_monitor_max_count_limit',
        hasFilters,
        selection: {
            selectedIdsSelector: domainMonitorSelectedIdsSelector,
            isSelectedAllSelector,
            selectAllOnPage,
            clearAll,
        },
        getAllIds: async() => (await getAllDomainMonitorIds({sort, filters})).data,
    });

    return {
        user,
        sort,
        pagination,
        isLoading,
        isFetching,
        refetch,
        activeCount,
        maxDomainMonitors: maxCount,
        list,
        handleCreate: create.open,
        isCreateOpen: create.isCreateOpen,
        isExceededOpen: create.isExceededOpen,
        closeCreate: create.close,
    };
};

export default useDomainMonitors;
